import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  Timer,
  Zap,
  Trophy,
  Heart,
  RotateCcw,
  CheckCircle,
  XCircle,
  ChevronRight,
  Volume2,
} from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useSoundEffects } from '../../../hooks/cabin/useSoundEffects';
import { useConfetti } from '../../../hooks/cabin/useConfetti';

/**
 * VocabularyTimeRace — 60-second timed vocab quiz
 *
 * phase.content schema:
 * { cards: [{ front, back, phonetic, image }], time_limit_seconds, lives, scoring }
 *
 * `image` opsiyonel — logo/gorsel tanima sorulari icin. Verildiginde soru
 * metninin ustunde gosterilir; metin yine basilir (gorsel yuklenmezse soru
 * tamamen kaybolmasin diye). alt metni CEVABI SIZDIRMAMALI, bu yuzden karttan
 * degil sabit bir etiketten gelir.
 */
export default function VocabularyTimeRace({
  phase,
  onPhaseComplete,
  'data-testid': testId = 'vocab-race',
}) {
  const { t } = useLanguage();
  const { play } = useSoundEffects();
  const { fire, fireSmall } = useConfetti();
  const shouldReduce = useReducedMotion();
  // Yuklenemeyen gorselin URL'i. Kart bazli karsilastirildigi icin sonraki
  // karta gecince kendiliginden gecersizlesir; ayri reset gerekmez.
  const [failedImage, setFailedImage] = useState(null);

  // Use raw array ref to avoid creating a new `[]` on every render which would
  // re-create startGame/handleAnswer callbacks via stale dep tracking.
  const rawCards = phase?.content?.cards;
  const cards = useMemo(() => rawCards || [], [rawCards]);
  const TIME_LIMIT = phase?.content?.time_limit_seconds || 60;
  const MAX_LIVES = phase?.content?.lives || 3;
  const maxXP = phase?.xp_in_phase || 0;

  const [gameState, setGameState] = useState('ready'); // ready | playing | finished
  const [currentIndex, setCurrentIndex] = useState(0);
  const [shuffledCards, setShuffledCards] = useState([]);
  const [options, setOptions] = useState([]);
  const [timeLeft, setTimeLeft] = useState(TIME_LIMIT);
  const [lives, setLives] = useState(MAX_LIVES);
  const [score, setScore] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [_feedback, setFeedback] = useState(null); // 'correct' | 'wrong'

  const timerRef = useRef(null);

  const generateOptions = useCallback(
    (cardArray, idx) => {
      const card = cardArray[idx];
      if (!card) return [];
      const correct = card.back;
      // Deduplicate distractor pool — exclude the correct answer value too
      const distractorPool = [
        ...new Set(
          cardArray
            .filter((_, i) => i !== idx)
            .map((c) => c.back)
            .filter((val) => val !== correct)
        ),
      ];
      const wrongs = distractorPool
        .sort(() => Math.random() - 0.5)
        .slice(0, 3);
      return [correct, ...wrongs].sort(() => Math.random() - 0.5);
    },
    []
  );

  const startGame = useCallback(() => {
    const shuffled = [...cards].sort(() => Math.random() - 0.5);
    setShuffledCards(shuffled);
    setOptions(generateOptions(shuffled, 0));
    setCurrentIndex(0);
    setTimeLeft(TIME_LIMIT);
    setLives(MAX_LIVES);
    setScore(0);
    setCorrectCount(0);
    setCombo(0);
    setMaxCombo(0);
    setSelectedAnswer(null);
    setFeedback(null);
    setGameState('playing');
    play('click');
  }, [cards, TIME_LIMIT, MAX_LIVES, generateOptions, play]);

  // Timer
  useEffect(() => {
    if (gameState !== 'playing') return;
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setGameState('finished');
          play('success');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [gameState, play]);

  const handleAnswer = useCallback(
    (answer) => {
      if (selectedAnswer !== null || gameState !== 'playing') return;

      const card = shuffledCards[currentIndex];
      const correct = answer === card.back;
      setSelectedAnswer(answer);
      setFeedback(correct ? 'correct' : 'wrong');

      if (correct) {
        play('correct');
        if (!shouldReduce) fireSmall({ y: 0.5 });

        const comboBonus = combo * 5;
        const timeBonus = Math.ceil(timeLeft / 10);
        const pts = 10 + timeBonus + comboBonus;
        setScore((s) => s + pts);
        setCorrectCount((c) => c + 1);
        setCombo((c) => {
          const newC = c + 1;
          setMaxCombo((m) => Math.max(m, newC));
          return newC;
        });
      } else {
        play('wrong');
        setCombo(0);
        setLives((l) => {
          const newL = l - 1;
          if (newL <= 0) {
            clearInterval(timerRef.current);
            setTimeout(() => setGameState('finished'), 600);
          }
          return newL;
        });
      }

      setTimeout(
        () => {
          const next = currentIndex + 1;
          if (next >= shuffledCards.length) {
            setGameState('finished');
            play('success');
            if (!shouldReduce) fire({ particleCount: 80 });
          } else {
            setCurrentIndex(next);
            setOptions(generateOptions(shuffledCards, next));
            setSelectedAnswer(null);
            setFeedback(null);
          }
        },
        shouldReduce ? 0 : 600
      );
    },
    [
      selectedAnswer,
      gameState,
      shuffledCards,
      currentIndex,
      combo,
      timeLeft,
      play,
      shouldReduce,
      fireSmall,
      fire,
      generateOptions,
    ]
  );

  const handlePlayWord = useCallback(() => {
    const word = shuffledCards[currentIndex]?.front || '';
    if ('speechSynthesis' in window) {
      const utt = new window.SpeechSynthesisUtterance(word);
      utt.lang = 'en-US';
      utt.rate = 0.9;
      window.speechSynthesis.speak(utt);
    }
  }, [shuffledCards, currentIndex]);

  // Ready screen
  if (gameState === 'ready') {
    return (
      <div
        className="flex flex-col gap-6 w-full max-w-lg mx-auto"
        data-testid={`${testId}-ready`}
      >
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-7 text-white text-center shadow-[0_6px_0_0_#4338ca]">
          <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-4">
            <Timer size={32} aria-hidden="true" />
          </div>
          <h2 className="text-2xl font-black mb-2">
            {t('cabin.exercise.timeRace.title')}
          </h2>
          <p className="text-white/80 text-sm mb-5">
            {t('cabin.exercise.timeRace.cards').replace('{count}', cards.length)}
          </p>

          <div className="bg-white/10 rounded-xl p-4 text-left text-sm text-white/90 space-y-1.5">
            <p>• {t('cabin.exercise.timeRace.rule1').replace('{time}', TIME_LIMIT)}</p>
            <p>• {t('cabin.exercise.timeRace.rule2')}</p>
            <p>• {t('cabin.exercise.timeRace.rule3').replace('{lives}', MAX_LIVES)}</p>
            <p>• {t('cabin.exercise.timeRace.rule4')}</p>
          </div>
        </div>

        <motion.button
          whileHover={shouldReduce ? {} : { scale: 1.02 }}
          whileTap={shouldReduce ? {} : { scale: 0.98 }}
          onClick={startGame}
          className="w-full py-4 bg-[var(--brand-primary)] text-white font-black text-lg rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
          data-testid={`${testId}-start`}
        >
          <span className="flex items-center justify-center gap-2">
            <Zap size={20} aria-hidden="true" />
            {t('cabin.exercise.timeRace.start')}
          </span>
        </motion.button>
      </div>
    );
  }

  // Finished screen
  if (gameState === 'finished') {
    const accuracy =
      shuffledCards.length > 0
        ? Math.round((correctCount / Math.min(currentIndex + 1, shuffledCards.length)) * 100)
        : 0;
    const finalScore = maxXP > 0 ? Math.round((score / Math.max(score, 1)) * maxXP * (accuracy / 100)) : score;

    const grade =
      accuracy >= 90 ? 'S' : accuracy >= 80 ? 'A' : accuracy >= 70 ? 'B' : accuracy >= 60 ? 'C' : 'D';
    const gradeColors = {
      S: 'from-amber-400 to-amber-500',
      A: 'from-emerald-400 to-emerald-500',
      B: 'from-sky-400 to-sky-500',
      C: 'from-purple-400 to-purple-500',
      D: 'from-gray-400 to-gray-500',
    };

    return (
      <motion.div
        initial={shouldReduce ? {} : { opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center gap-5 w-full max-w-lg mx-auto text-center py-4"
        data-testid={`${testId}-finish`}
      >
        <div
          className={`w-20 h-20 rounded-full flex items-center justify-center bg-gradient-to-br ${gradeColors[grade]} shadow-[0_4px_0_0_rgba(0,0,0,0.15)]`}
        >
          <span className="text-4xl font-black text-white">{grade}</span>
        </div>

        <div>
          <h2 className="text-2xl font-black text-[var(--text-main)] mb-1">
            {accuracy >= 80
              ? t('cabin.exercise.timeRace.great')
              : accuracy >= 60
              ? t('cabin.exercise.timeRace.good')
              : t('cabin.exercise.timeRace.tryAgain')}
          </h2>
          <p className="text-sm text-[var(--text-muted)]">
            {t('cabin.exercise.timeRace.gameOver')}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 w-full">
          {[
            { icon: Trophy, label: t('cabin.exercise.timeRace.score'), value: score, color: 'yellow' },
            { icon: CheckCircle, label: t('cabin.exercise.timeRace.correct'), value: `${correctCount}/${shuffledCards.length}`, color: 'green' },
            { icon: Zap, label: t('cabin.exercise.timeRace.maxCombo'), value: `x${maxCombo}`, color: 'purple' },
            { icon: Timer, label: t('cabin.exercise.timeRace.accuracy'), value: `${accuracy}%`, color: 'blue' },
          ].map(({ icon: Icon, label, value, color }) => (
            <div
              key={label}
              className={`bg-${color}-50 border-2 border-${color}-200 rounded-2xl p-4 flex flex-col items-center gap-1`}
            >
              <Icon size={20} className={`text-${color}-500`} aria-hidden="true" />
              <span className="text-xl font-black text-[var(--text-main)]">{value}</span>
              <span className="text-xs text-[var(--text-muted)]">{label}</span>
            </div>
          ))}
        </div>

        <div className="flex gap-3 w-full">
          <motion.button
            whileHover={shouldReduce ? {} : { scale: 1.02 }}
            whileTap={shouldReduce ? {} : { scale: 0.98 }}
            onClick={startGame}
            className="flex-1 flex items-center justify-center gap-2 py-3 border-2 border-indigo-300 text-indigo-600 hover:bg-indigo-50 rounded-xl font-bold text-sm transition-colors"
          >
            <RotateCcw size={16} aria-hidden="true" />
            {t('cabin.exercise.timeRace.playAgain')}
          </motion.button>
          <motion.button
            whileHover={shouldReduce ? {} : { scale: 1.02 }}
            whileTap={shouldReduce ? {} : { scale: 0.98 }}
            onClick={() =>
              onPhaseComplete({
                score: Math.min(finalScore, maxXP),
                maxScore: maxXP,
                answers: { correct: correctCount, total: shuffledCards.length, maxCombo },
                timeSpent: TIME_LIMIT - timeLeft,
              })
            }
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-[var(--brand-primary)] text-white font-bold rounded-xl shadow-[0_3px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] transition-colors"
            data-testid={`${testId}-complete`}
          >
            {t('exercise.finish')}
            <ChevronRight size={16} aria-hidden="true" />
          </motion.button>
        </div>
      </motion.div>
    );
  }

  // Playing
  const card = shuffledCards[currentIndex];
  const timePct = (timeLeft / TIME_LIMIT) * 100;
  const timeColor =
    timeLeft > TIME_LIMIT * 0.5
      ? 'bg-emerald-500'
      : timeLeft > TIME_LIMIT * 0.2
      ? 'bg-amber-500'
      : 'bg-rose-500';

  return (
    <div
      className="flex flex-col gap-4 w-full max-w-lg mx-auto"
      data-testid={`${testId}-playing`}
    >
      {/* Timer bar */}
      <div>
        <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden border border-gray-300">
          <motion.div
            className={`h-full rounded-full transition-all ease-linear ${timeColor}`}
            animate={{ width: `${timePct}%` }}
            transition={{ duration: 1, ease: 'linear' }}
          />
          <span className="absolute inset-0 flex items-center justify-center text-xs font-black text-white drop-shadow">
            {timeLeft}s
          </span>
        </div>
      </div>

      {/* Stats row */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {Array.from({ length: MAX_LIVES }).map((_, i) => (
            <motion.span
              key={i}
              animate={i >= lives && !shouldReduce ? { scale: [1, 1.3, 0.8, 1] } : {}}
              transition={{ duration: 0.3 }}
            >
              <Heart
                size={18}
                className={i < lives ? 'text-rose-500 fill-rose-500' : 'text-gray-300 fill-gray-200'}
                aria-hidden="true"
              />
            </motion.span>
          ))}
        </div>

        <AnimatePresence>
          {combo >= 2 && (
            <motion.div
              initial={shouldReduce ? {} : { scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              className="flex items-center gap-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-black"
            >
              <Zap size={12} aria-hidden="true" />
              x{combo} {t('cabin.exercise.timeRace.combo')}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex items-center gap-1 bg-amber-100 border border-amber-200 px-3 py-1 rounded-full">
          <Trophy size={14} className="text-amber-600" aria-hidden="true" />
          <span className="text-xs font-black text-amber-700">{score}</span>
        </div>
      </div>

      {/* Question card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={shouldReduce ? {} : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={shouldReduce ? {} : { opacity: 0, y: -10 }}
          transition={{ duration: 0.15 }}
          className="bg-white border-2 border-[var(--border-light)] rounded-2xl p-5 shadow-[0_4px_0_0_rgba(0,0,0,0.06)]"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-[var(--text-muted)]">
              {currentIndex + 1} / {shuffledCards.length}
            </span>
            <button
              onClick={handlePlayWord}
              aria-label={t('exercise.play_audio')}
              className="p-1.5 rounded-full bg-sky-50 hover:bg-sky-100 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
            >
              <Volume2 size={16} className="text-sky-500" aria-hidden="true" />
            </button>
          </div>
          {card?.image && failedImage !== card.image && (
            <div className="flex justify-center py-2">
              <img
                src={card.image}
                onError={() => setFailedImage(card.image)}
                alt={t('exercise.image_question_alt')}
                className="max-h-32 w-auto object-contain"
              />
            </div>
          )}
          <p className="text-3xl font-black text-center text-[var(--text-main)] py-3">
            {card?.front}
          </p>
          {card?.phonetic && (
            <p className="text-center text-xs text-[var(--text-muted)] mt-1">{card.phonetic}</p>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Options 2x2 grid */}
      <div className="grid grid-cols-2 gap-2.5">
        {options.map((opt, idx) => {
          const isSelected = selectedAnswer === opt;
          const isCorrectOpt = card?.back === opt;
          let cls =
            'border-2 border-[var(--border-light)] bg-white hover:border-[var(--brand-primary)] hover:bg-sky-50 text-[var(--text-main)]';
          if (selectedAnswer !== null) {
            if (isCorrectOpt)
              cls = 'border-2 border-[#10B981] bg-[#10B981]/10 text-emerald-700';
            else if (isSelected && !isCorrectOpt)
              cls = 'border-2 border-rose-400 bg-rose-50 text-rose-700';
            else cls = 'border-2 border-gray-200 bg-gray-50 text-gray-400';
          }
          return (
            <motion.button
              key={idx}
              whileTap={selectedAnswer !== null || shouldReduce ? {} : { scale: 0.97 }}
              onClick={() => handleAnswer(opt)}
              disabled={selectedAnswer !== null}
              className={`p-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-1.5 min-h-[52px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] ${cls}`}
              data-testid={`${testId}-option-${idx}`}
            >
              {selectedAnswer !== null && isCorrectOpt && (
                <CheckCircle size={14} className="text-emerald-500 shrink-0" aria-hidden="true" />
              )}
              {selectedAnswer !== null && isSelected && !isCorrectOpt && (
                <XCircle size={14} className="text-rose-500 shrink-0" aria-hidden="true" />
              )}
              <span className="text-center">{opt}</span>
            </motion.button>
          );
        })}
      </div>

      {/* Progress dots */}
      <div className="flex justify-center gap-1 flex-wrap max-w-xs mx-auto">
        {shuffledCards.slice(0, 20).map((_, i) => (
          <div
            key={i}
            className={`w-2 h-2 rounded-full transition-all ${
              i < currentIndex
                ? 'bg-emerald-500'
                : i === currentIndex
                ? 'bg-[var(--brand-primary)] scale-125'
                : 'bg-gray-200'
            }`}
          />
        ))}
        {shuffledCards.length > 20 && (
          <span className="text-xs text-[var(--text-muted)]">+{shuffledCards.length - 20}</span>
        )}
      </div>
    </div>
  );
}
